import type { BillingPlan, CaseBundle, EvidenceCase, EvidenceEntry, User } from "../../shared/types";

type ApiOptions = Omit<RequestInit, "body"> & {
  body?: BodyInit | Record<string, unknown> | null;
};

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  let body = options.body;
  if (body && !(body instanceof FormData) && typeof body !== "string") {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(body);
  }

  const response = await fetch(path, {
    ...options,
    headers,
    body,
    credentials: "include"
  });
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const message = typeof payload === "object" && payload && "error" in payload ? String(payload.error) : "Request failed";
    throw new Error(message);
  }
  return payload as T;
}

export const client = {
  me: () => api<{ user: User | null }>("/api/auth/me"),
  register: (input: { email: string; password: string; name: string; deviceId: string }) =>
    api<{ user: User }>("/api/auth/register", { method: "POST", body: input }),
  login: (input: { email: string; password: string }) => api<{ user: User }>("/api/auth/login", { method: "POST", body: input }),
  logout: () => api<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  cases: () => api<{ cases: EvidenceCase[] }>("/api/cases"),
  createCase: (input: Partial<EvidenceCase>) => api<{ case: EvidenceCase }>("/api/cases", { method: "POST", body: input }),
  updateCase: (id: string, input: Partial<EvidenceCase>) =>
    api<{ case: EvidenceCase }>(`/api/cases/${id}`, { method: "PATCH", body: input }),
  getCase: (id: string) => api<CaseBundle>(`/api/cases/${id}`),
  addEvidence: (id: string, input: Omit<EvidenceEntry, "id" | "caseId" | "createdAt">) =>
    api<{ evidence: EvidenceEntry }>(`/api/cases/${id}/evidence`, { method: "POST", body: input }),
  generateLetter: (id: string) =>
    api<{ guidance: { summary: string; nextActions: string[]; riskFlags: string[]; landlordLetter: string; model: string } }>(
      `/api/cases/${id}/generate-letter`,
      { method: "POST" }
    ),
  billingConfig: () =>
    api<{ publishableKey: string; stripeReady: boolean; webhookReady: boolean; plans: BillingPlan[] }>("/api/billing/config"),
  billingMe: () => api<{ billing: { planCode: string; billingStatus: string; purchases: unknown[] } }>("/api/billing/me"),
  checkout: (planCode: string) => api<{ url: string }>("/api/billing/checkout/session", { method: "POST", body: { planCode } }),
  confirmCheckout: (sessionId: string) => api<{ status: string; paymentStatus: string }>(`/api/billing/confirm?session_id=${sessionId}`),
  portal: () => api<{ url: string }>("/api/billing/portal/session", { method: "POST" }),
  adminUsers: () => api<{ users: Array<Record<string, unknown>> }>("/api/admin/users"),
  adminGrantPlan: (userId: string, planCode: string) =>
    api<{ ok: true }>("/api/admin/grant-plan", { method: "POST", body: { userId, planCode } }),
  adminSetRole: (userId: string, role: string) => api<{ ok: true }>("/api/admin/set-role", { method: "POST", body: { userId, role } })
};
